import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { CreditScoreHistory } from '../../types/creditScore';
import { TrendingUp, TrendingDown, Calendar } from 'lucide-react';
interface ScoreHistoryChartProps {
  history: CreditScoreHistory[];
}
export function ScoreHistoryChart({
  history
}: ScoreHistoryChartProps) {
  // Calculate overall trend
  const firstScore = history[0]?.generalScore || 0;
  const lastScore = history[history.length - 1]?.generalScore || 0;
  const scoreDiff = lastScore - firstScore;
  const trendPercentage = firstScore > 0 ? (scoreDiff / firstScore * 100).toFixed(1) : '0';
  const isPositiveTrend = scoreDiff >= 0;
  // Format data for chart
  const chartData = history.map(h => ({
    date: new Date(h.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    generalScore: h.generalScore,
    rentScore: h.rentScore,
    fullDate: new Date(h.date).toLocaleDateString()
  }));
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-860">
      <div className="flex items-center justify-between mb-6" data-id="element-861">
        <div data-id="element-862">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-863">Score History</h3>
          <p className="text-sm text-gray-500 mt-1" data-id="element-864">
            Track your credit score over time
          </p>
        </div>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${isPositiveTrend ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-865">
          {isPositiveTrend ? <TrendingUp className="h-4 w-4" data-id="element-866" /> : <TrendingDown className="h-4 w-4" data-id="element-867" />}
          <span className="text-sm font-bold" data-id="element-868">
            {isPositiveTrend ? '+' : ''}
            {trendPercentage}%
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="h-64 mb-6" data-id="element-869">
        <ResponsiveContainer width="100%" height="100%" data-id="element-870">
          <AreaChart data={chartData} data-id="element-871">
            <defs data-id="element-872">
              <linearGradient id="generalScoreGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-873">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-874" />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-875" />
              </linearGradient>
              <linearGradient id="rentScoreGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-876">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-877" />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-878" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-879" />
            <XAxis dataKey="date" stroke="#6b7280" style={{
            fontSize: '12px'
          }} data-id="element-880" />
            <YAxis domain={[0, 850]} stroke="#6b7280" style={{
            fontSize: '12px'
          }} data-id="element-881" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }} data-id="element-882" />
            <Area type="monotone" dataKey="generalScore" stroke="#3b82f6" strokeWidth={3} fill="url(#generalScoreGradient)" name="General Score" data-id="element-883" />
            <Area type="monotone" dataKey="rentScore" stroke="#10b981" strokeWidth={3} fill="url(#rentScoreGradient)" name="Rent Score" data-id="element-884" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 mb-6" data-id="element-885">
        <div className="flex items-center space-x-2" data-id="element-886">
          <div className="w-3 h-3 bg-blue-500 rounded-full" data-id="element-887"></div>
          <span className="text-sm text-gray-600" data-id="element-888">General Score</span>
        </div>
        <div className="flex items-center space-x-2" data-id="element-889">
          <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-890"></div>
          <span className="text-sm text-gray-600" data-id="element-891">Rent Score (R-Score)</span>
        </div>
      </div>

      {/* Recent Changes */}
      <div className="space-y-3" data-id="element-892">
        <h4 className="font-semibold text-gray-900 text-sm" data-id="element-893">Recent Changes</h4>
        {history.slice(-3).reverse().map((item, index) => <motion.div key={item.date} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: index * 0.1
      }} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-894">
              <div className="flex items-center space-x-3" data-id="element-895">
                <Calendar className="h-4 w-4 text-gray-400" data-id="element-896" />
                <div data-id="element-897">
                  <p className="text-sm font-medium text-gray-900" data-id="element-898">
                    {new Date(item.date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-899">{item.reason}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2" data-id="element-900">
                <span className="text-lg font-bold text-gray-900" data-id="element-901">
                  {item.generalScore}
                </span>
                <div className={`flex items-center space-x-1 ${item.change >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-902">
                  {item.change >= 0 ? <TrendingUp className="h-4 w-4" data-id="element-903" /> : <TrendingDown className="h-4 w-4" data-id="element-904" />}
                  <span className="text-sm font-bold" data-id="element-905">
                    {item.change >= 0 ? '+' : ''}
                    {item.change}
                  </span>
                </div>
              </div>
            </motion.div>)}
      </div>
    </div>;
}